import {
  AddSidebarSpaceRequest,
  BulkDereferenceObjectsRequest,
  BulkOwnObjectsRequest,
  BulkReferenceObjectsRequest,
  CreateSectionCardRequest,
  CreateSpaceRequest,
  CreateSpaceSectionRequest,
  DeleteSectionCardRequest,
  DeleteSpaceRequest,
  DeleteSpaceSectionRequest,
  DereferenceObjectRequest,
  DuplicateSpaceSectionRequest,
  GetAllSpacesRequest,
  GetDefaultSpaceRequest,
  GetLibraryViewRequest,
  GetPersonalizedSidebarRequest,
  GetSpaceCardViewRequest,
  GetSpaceForObjectRequest,
  GetSpaceRequest,
  MoveSectionCardRequest,
  MoveSpaceSectionRequest,
  ObjectType,
  OwnObjectRequest,
  ReferenceObjectRequest,
  RemoveSidebarSpaceRequest,
  SpacesPublic,
  UpdateSectionCardRequest,
  UpdateSpaceRequest,
  UpdateSpaceSectionRequest,
} from '@fullstory/proto/fs/services/dx-objects';
import {
  ActionType,
  createAction,
  createGRPCAction,
  RequestActionType,
} from '@fullstory/redux-util';
import { SpaceRequestTypes, SpaceStateTypes } from './types';
import { SeatSettingsPublic, SetSpacesEnabledRequest } from '@fullstory/proto/fs/services/webber';

// Actions performed to the local redux store which don't require a server call
export const SpaceStateActions = {
  updateObjectSpaceAssignment: (request: {
    dxObjectType: ObjectType;
    dxObjectId: string;
    newSpace: string;
  }) =>
    createAction({
      type: SpaceStateTypes.MoveObjectToSpace,
      request,
    }),
};

export const SpaceRequestActions = {
  createSpace: (req: CreateSpaceRequest) =>
    createGRPCAction<typeof SpacesPublic.CreateSpace>()(
      {
        type: SpaceRequestTypes.CreateSpaceRequest,
        grpcFunction: SpacesPublic.CreateSpace,
        body: req,
        force: true,
        readWrite: 'write',
      },
      SpaceRequestTypes.CreateSpaceSuccess,
      SpaceRequestTypes.CreateSpaceFailure,
    ),
  getSpace: (req: GetSpaceRequest, force: boolean = false) =>
    createGRPCAction<typeof SpacesPublic.GetSpace>()(
      {
        type: SpaceRequestTypes.GetSpaceRequest,
        grpcFunction: SpacesPublic.GetSpace,
        body: req,
        readWrite: 'read',
        force,
      },
      SpaceRequestTypes.GetSpaceSuccess,
      SpaceRequestTypes.GetSpaceFailure,
    ),
  getDefaultSpace: (req: GetDefaultSpaceRequest, force: boolean = false) =>
    createGRPCAction<typeof SpacesPublic.GetDefaultSpace>()(
      {
        type: SpaceRequestTypes.GetDefaultSpaceRequest,
        grpcFunction: SpacesPublic.GetDefaultSpace,
        body: req,
        readWrite: 'read',
        force,
      },
      SpaceRequestTypes.GetDefaultSpaceSuccess,
      SpaceRequestTypes.GetDefaultSpaceFailure,
    ),
  getAllSpaces: (req: GetAllSpacesRequest, force: boolean = false) =>
    createGRPCAction<typeof SpacesPublic.GetAllSpaces>()(
      {
        type: SpaceRequestTypes.GetAllSpacesRequest,
        grpcFunction: SpacesPublic.GetAllSpaces,
        body: req,
        readWrite: 'read',
        force,
      },
      SpaceRequestTypes.GetAllSpacesSuccess,
      SpaceRequestTypes.GetAllSpacesFailure,
    ),
  updateSpace: (req: UpdateSpaceRequest) =>
    createGRPCAction<typeof SpacesPublic.UpdateSpace>()(
      {
        type: SpaceRequestTypes.UpdateSpaceRequest,
        grpcFunction: SpacesPublic.UpdateSpace,
        body: req,
        readWrite: 'write',
      },
      SpaceRequestTypes.UpdateSpaceSuccess,
      SpaceRequestTypes.UpdateSpaceFailure,
    ),
  deleteSpace: (req: DeleteSpaceRequest) =>
    createGRPCAction<typeof SpacesPublic.DeleteSpace>()(
      {
        type: SpaceRequestTypes.DeleteSpaceRequest,
        grpcFunction: SpacesPublic.DeleteSpace,
        body: req,
        force: true,
        readWrite: 'write',
      },
      SpaceRequestTypes.DeleteSpaceSuccess,
      SpaceRequestTypes.DeleteSpaceFailure,
    ),
  getSpaceCardView: (req: GetSpaceCardViewRequest, force: boolean = false) =>
    createGRPCAction<typeof SpacesPublic.GetSpaceCardView>()(
      {
        type: SpaceRequestTypes.GetSpaceCardViewRequest,
        grpcFunction: SpacesPublic.GetSpaceCardView,
        body: req,
        readWrite: 'read',
        force,
      },
      SpaceRequestTypes.GetSpaceCardViewSuccess,
      SpaceRequestTypes.GetSpaceCardViewFailure,
    ),
  getLibraryView: (req: GetLibraryViewRequest, force: boolean = false) =>
    createGRPCAction<typeof SpacesPublic.GetLibraryView>()(
      {
        type: SpaceRequestTypes.GetLibraryViewRequest,
        grpcFunction: SpacesPublic.GetLibraryView,
        body: req,
        readWrite: 'read',
        force,
        settings: {
          headers: { 'GRPC-Timeout': '1M' }, // allow for 1 minute before context deadline exceeded
        },
      },
      SpaceRequestTypes.GetLibraryViewSuccess,
      SpaceRequestTypes.GetLibraryViewFailure,
    ),
  createSection: (req: CreateSpaceSectionRequest) =>
    createGRPCAction<typeof SpacesPublic.CreateSpaceSection>()(
      {
        type: SpaceRequestTypes.CreateSectionRequest,
        grpcFunction: SpacesPublic.CreateSpaceSection,
        body: req,
        readWrite: 'write',
        force: true,
      },
      SpaceRequestTypes.CreateSectionSuccess,
      SpaceRequestTypes.CreateSectionFailure,
    ),
  duplicateSection: (req: DuplicateSpaceSectionRequest) =>
    createGRPCAction<typeof SpacesPublic.DuplicateSpaceSection>()(
      {
        type: SpaceRequestTypes.DuplicateSectionRequest,
        grpcFunction: SpacesPublic.DuplicateSpaceSection,
        body: req,
        readWrite: 'write',
        force: true,
      },
      SpaceRequestTypes.DuplicateSectionSuccess,
      SpaceRequestTypes.DuplicateSectionFailure,
    ),
  updateSection: (req: UpdateSpaceSectionRequest) =>
    createGRPCAction<typeof SpacesPublic.UpdateSpaceSection>()(
      {
        type: SpaceRequestTypes.UpdateSectionRequest,
        grpcFunction: SpacesPublic.UpdateSpaceSection,
        body: req,
        readWrite: 'write',
      },
      SpaceRequestTypes.UpdateSectionSuccess,
      SpaceRequestTypes.UpdateSectionFailure,
    ),
  deleteSection: (req: DeleteSpaceSectionRequest) =>
    createGRPCAction<typeof SpacesPublic.DeleteSpaceSection>()(
      {
        type: SpaceRequestTypes.DeleteSectionRequest,
        grpcFunction: SpacesPublic.DeleteSpaceSection,
        body: req,
        readWrite: 'write',
        force: true,
      },
      SpaceRequestTypes.DeleteSectionSuccess,
      SpaceRequestTypes.DeleteSectionFailure,
    ),
  moveSection: (req: MoveSpaceSectionRequest) =>
    createGRPCAction<typeof SpacesPublic.MoveSpaceSection>()(
      {
        type: SpaceRequestTypes.MoveSectionRequest,
        grpcFunction: SpacesPublic.MoveSpaceSection,
        body: req,
        readWrite: 'write',
        force: true,
      },
      SpaceRequestTypes.MoveSectionSuccess,
      SpaceRequestTypes.MoveSectionFailure,
    ),

  createCard: (req: CreateSectionCardRequest) =>
    createGRPCAction<typeof SpacesPublic.CreateSectionCard>()(
      {
        type: SpaceRequestTypes.CreateCardRequest,
        grpcFunction: SpacesPublic.CreateSectionCard,
        body: req,
        readWrite: 'write',
        force: true,
      },
      SpaceRequestTypes.CreateCardSuccess,
      SpaceRequestTypes.CreateCardFailure,
    ),
  updateCard: (req: UpdateSectionCardRequest) =>
    createGRPCAction<typeof SpacesPublic.UpdateSectionCard>()(
      {
        type: SpaceRequestTypes.UpdateCardRequest,
        grpcFunction: SpacesPublic.UpdateSectionCard,
        body: req,
        readWrite: 'write',
        force: true,
      },
      SpaceRequestTypes.UpdateCardSuccess,
      SpaceRequestTypes.UpdateCardFailure,
    ),
  deleteCard: (req: DeleteSectionCardRequest) =>
    createGRPCAction<typeof SpacesPublic.DeleteSectionCard>()(
      {
        type: SpaceRequestTypes.DeleteCardRequest,
        grpcFunction: SpacesPublic.DeleteSectionCard,
        body: req,
        readWrite: 'write',
      },
      SpaceRequestTypes.DeleteCardSuccess,
      SpaceRequestTypes.DeleteCardFailure,
    ),
  getPersonalizedSidebar: (req: GetPersonalizedSidebarRequest, force: boolean = false) =>
    createGRPCAction<typeof SpacesPublic.GetPersonalizedSidebar>()(
      {
        type: SpaceRequestTypes.GetPersonalizedSidebarRequest,
        grpcFunction: SpacesPublic.GetPersonalizedSidebar,
        body: req,
        readWrite: 'write',
        force,
        settings: {
          headers: { 'GRPC-Timeout': '1M' }, // allow for 1 minute before context deadline exceeded
        },
      },
      SpaceRequestTypes.GetPersonalizedSidebarSuccess,
      SpaceRequestTypes.GetPersonalizedSidebarFailure,
    ),
  addSpaceToSidebar: (req: AddSidebarSpaceRequest, force: boolean = true) =>
    createGRPCAction<typeof SpacesPublic.AddSidebarSpace>()(
      {
        type: SpaceRequestTypes.AddSidebarSpaceRequest,
        grpcFunction: SpacesPublic.AddSidebarSpace,
        body: req,
        readWrite: 'write',
        force,
        settings: {
          headers: { 'GRPC-Timeout': '1M' }, // allow for 1 minute before context deadline exceeded
        },
      },
      SpaceRequestTypes.AddSidebarSpaceSuccess,
      SpaceRequestTypes.AddSidebarSpaceFailure,
    ),
  removeSpaceFromSidebar: (req: RemoveSidebarSpaceRequest, force: boolean = true) =>
    createGRPCAction<typeof SpacesPublic.RemoveSidebarSpace>()(
      {
        type: SpaceRequestTypes.RemoveSidebarSpaceRequest,
        grpcFunction: SpacesPublic.RemoveSidebarSpace,
        body: req,
        readWrite: 'write',
        force,
        settings: {
          headers: { 'GRPC-Timeout': '1M' }, // allow for 1 minute before context deadline exceeded
        },
      },
      SpaceRequestTypes.RemoveSidebarSpaceSuccess,
      SpaceRequestTypes.RemoveSidebarSpaceFailure,
    ),

  moveCard: (req: MoveSectionCardRequest) =>
    createGRPCAction<typeof SpacesPublic.MoveSectionCard>()(
      {
        type: SpaceRequestTypes.MoveCardRequest,
        grpcFunction: SpacesPublic.MoveSectionCard,
        body: req,
        readWrite: 'write',
        force: true,
      },
      SpaceRequestTypes.MoveCardSuccess,
      SpaceRequestTypes.MoveCardFailure,
    ),
  getObjectSpace: (req: GetSpaceForObjectRequest, force = false) =>
    createGRPCAction<typeof SpacesPublic.GetSpaceForObject>()(
      {
        type: SpaceRequestTypes.GetObjectSpaceRequest,
        grpcFunction: SpacesPublic.GetSpaceForObject,
        body: req,
        force,
        readWrite: 'read',
      },
      SpaceRequestTypes.GetObjectSpaceSuccess,
      SpaceRequestTypes.GetObjectSpaceFailure,
    ),
  moveObjectToSpace: (req: OwnObjectRequest) =>
    createGRPCAction<typeof SpacesPublic.OwnObject>()(
      {
        type: SpaceRequestTypes.MoveObjectRequest,
        grpcFunction: SpacesPublic.OwnObject,
        body: req,
        force: true,
        readWrite: 'write',
      },
      SpaceRequestTypes.MoveObjectSuccess,
      SpaceRequestTypes.MoveObjectFailure,
    ),
  moveObjectsToSpace: (req: BulkOwnObjectsRequest) =>
    createGRPCAction<typeof SpacesPublic.BulkOwnObjects>()(
      {
        type: SpaceRequestTypes.MoveObjectsRequest,
        grpcFunction: SpacesPublic.BulkOwnObjects,
        body: req,
        force: true,
        readWrite: 'write',
      },
      SpaceRequestTypes.MoveObjectsSuccess,
      SpaceRequestTypes.MoveObjectsFailure,
    ),
  linkObjectToSpace: (req: ReferenceObjectRequest) =>
    createGRPCAction<typeof SpacesPublic.ReferenceObject>()(
      {
        type: SpaceRequestTypes.LinkObjectRequest,
        grpcFunction: SpacesPublic.ReferenceObject,
        body: req,
        force: true,
        readWrite: 'write',
      },
      SpaceRequestTypes.LinkObjectSuccess,
      SpaceRequestTypes.LinkObjectFailure,
    ),
  bulkLinkObjectsToSpace: (req: BulkReferenceObjectsRequest) =>
    createGRPCAction<typeof SpacesPublic.BulkReferenceObjects>()(
      {
        type: SpaceRequestTypes.BulkLinkObjectsRequest,
        grpcFunction: SpacesPublic.BulkReferenceObjects,
        body: req,
        force: true,
        readWrite: 'write',
      },
      SpaceRequestTypes.BulkLinkObjectsSuccess,
      SpaceRequestTypes.BulkLinkObjectsFailure,
    ),
  dereferenceObject: (req: DereferenceObjectRequest) =>
    createGRPCAction<typeof SpacesPublic.DereferenceObject>()(
      {
        type: SpaceRequestTypes.DereferenceObjectRequest,
        grpcFunction: SpacesPublic.DereferenceObject,
        body: req,
        force: true,
        readWrite: 'write',
      },
      SpaceRequestTypes.DereferenceObjectSuccess,
      SpaceRequestTypes.DereferenceObjectFailure,
    ),
  bulkDereferenceObject: (req: BulkDereferenceObjectsRequest) =>
    createGRPCAction<typeof SpacesPublic.BulkDereferenceObjects>()(
      {
        type: SpaceRequestTypes.BulkDereferenceObjectsRequest,
        grpcFunction: SpacesPublic.BulkDereferenceObjects,
        body: req,
        force: true,
        readWrite: 'write',
      },
      SpaceRequestTypes.BulkDereferenceObjectsSuccess,
      SpaceRequestTypes.BulkDereferenceObjectsFailure,
    ),

  setSpacesEnabled: (req: SetSpacesEnabledRequest) =>
    createGRPCAction<typeof SeatSettingsPublic.SetSpacesEnabled>()(
      {
        type: SpaceRequestTypes.SetSpacesEnabledRequest,
        grpcFunction: SeatSettingsPublic.SetSpacesEnabled,
        body: req,
        force: true,
        readWrite: 'write',
      },
      SpaceRequestTypes.SetSpacesEnabledSuccess,
      SpaceRequestTypes.SetSpacesEnabledFailure,
    ),
};

export type SpaceActions =
  | RequestActionType<typeof SpaceRequestActions>
  | ActionType<typeof SpaceStateActions>;
